import { useState, useEffect } from 'react';
import { Document, Packer, Paragraph, TextRun, AlignmentType, PageBreak } from 'docx';
import { Download, FileText, Loader2 } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Input';
import { Switch } from '@/components/ui/Switch';
import { Dropzone } from '@/components/ui/Dropzone';
import { Badge } from '@/components/ui/Badge';
import { useApp } from '@/store/app';
import { saveAs } from 'file-saver';
import { toast } from 'sonner';
import { readFileAsArrayBuffer } from '@/lib/utils';
import { pdfjs } from '@/lib/pdfjs';

/**
 * Strip characters that break OOXML schema validation. Word is forgiving;
 * Google Docs is strict and refuses to upload a doc containing any of these.
 *
 *   - C0 controls (preserve only \t \n \r)
 *   - DEL + C1 controls
 *   - Byte-order mark
 *   - Reserved / non-character codepoints (U+FFFE, U+FFFF)
 *   - Unpaired UTF-16 surrogates (would crash OOXML serializers)
 *
 * Implemented as an explicit code-point loop so the source stays pure ASCII
 * (literal control bytes inside regex literals make git treat the file as
 * binary, which kills code review).
 */
function sanitizeText(s: string): string {
  if (!s) return '';
  let out = '';
  for (let i = 0; i < s.length; i++) {
    const code = s.charCodeAt(i);

    // C0 controls except \t (9), \n (10), \r (13)
    if (code <= 8 || code === 11 || code === 12 || (code >= 14 && code <= 31)) continue;
    // DEL + C1 controls
    if (code >= 127 && code <= 159) continue;
    // BOM
    if (code === 0xfeff) continue;
    // Non-character codepoints
    if (code === 0xfffe || code === 0xffff) continue;

    // High surrogate — must be followed by a low surrogate
    if (code >= 0xd800 && code <= 0xdbff) {
      const low = s.charCodeAt(i + 1);
      if (low >= 0xdc00 && low <= 0xdfff) {
        out += s.charAt(i) + s.charAt(i + 1);
        i++;
      }
      // else: unpaired high surrogate, drop it
      continue;
    }
    // Unpaired low surrogate (we'd have consumed paired ones above)
    if (code >= 0xdc00 && code <= 0xdfff) continue;

    out += s.charAt(i);
  }
  return out.replace(/\s+/g, ' ').trim();
}

/** Keep font sizes inside a sane Word range — pdfjs occasionally reports
 *  sub-1pt sizes (which render invisible) or massive sizes from glyph
 *  decorations. Clamp to 8pt..36pt. */
function clampSize(size: number): number {
  if (!Number.isFinite(size) || size <= 0) return 11;
  return Math.max(8, Math.min(36, size));
}

interface PdfLine {
  text: string;
  size: number;
  bold: boolean;
  italic: boolean;
  page: number;
}

export function PdfToWord() {
  const { pendingFile, setPendingFile } = useApp();
  const [file, setFile] = useState<File | null>(null);
  const [lines, setLines] = useState<PdfLine[]>([]);
  const [working, setWorking] = useState(false);
  const [progress, setProgress] = useState(0);
  const [keepBreaks, setKeepBreaks] = useState(true);
  const [detectHeadings, setDetectHeadings] = useState(true);

  useEffect(() => {
    if (pendingFile && pendingFile.type === 'application/pdf') {
      handleFile([pendingFile]);
      setPendingFile(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleFile(files: File[]) {
    const f = files[0];
    if (!f) return;
    setFile(f);
    setLines([]);
    setProgress(0);
    setWorking(true);
    try {
      const buf = await readFileAsArrayBuffer(f);
      const doc = await pdfjs.getDocument({ data: buf }).promise;
      const out: PdfLine[] = [];
      for (let i = 1; i <= doc.numPages; i++) {
        const page = await doc.getPage(i);
        const textContent = await page.getTextContent();

        const buckets = new Map<
          number,
          { items: { x: number; str: string; size: number; bold: boolean; italic: boolean }[]; size: number }
        >();
        for (const item of textContent.items) {
          if (!('str' in item)) continue;
          const str = item.str as string;
          if (!str) continue;
          const trans = item.transform as number[];
          const fontSize = Math.abs(trans[3]) || 11;
          const yKey = Math.round(trans[5] / 2) * 2;
          const fontName = ('fontName' in item ? (item.fontName as string) : '') || '';
          const bold = /bold/i.test(fontName);
          const italic = /italic|oblique/i.test(fontName);
          const arr = buckets.get(yKey) ?? { items: [], size: fontSize };
          arr.items.push({ x: trans[4], str, size: fontSize, bold, italic });
          arr.size = Math.max(arr.size, fontSize);
          buckets.set(yKey, arr);
        }

        const sortedY = [...buckets.keys()].sort((a, b) => b - a);
        for (const y of sortedY) {
          const bucket = buckets.get(y)!;
          bucket.items.sort((a, b) => a.x - b.x);
          let line = '';
          let lastX = -Infinity;
          let lineBold = bucket.items.length > 0;
          let lineItalic = bucket.items.length > 0;
          for (const it of bucket.items) {
            if (lastX > -Infinity && it.x - lastX > bucket.size * 0.5) line += ' ';
            line += it.str;
            lastX = it.x + it.str.length * bucket.size * 0.5;
            if (!it.bold) lineBold = false;
            if (!it.italic) lineItalic = false;
          }
          line = line.replace(/\s+/g, ' ').trim();
          if (!line) continue;
          out.push({
            text: line,
            size: bucket.size,
            bold: lineBold,
            italic: lineItalic,
            page: i,
          });
        }
        setProgress(Math.round((i / doc.numPages) * 100));
      }
      setLines(out);
      toast.success(`Extracted ${out.length} lines from ${doc.numPages} page${doc.numPages === 1 ? '' : 's'}`);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('PDF parse failed:', e);
      toast.error('Could not parse PDF — it may be image-based (run OCR first)');
    } finally {
      setWorking(false);
    }
  }

  async function exportDocx() {
    if (!lines.length || !file) return;
    setWorking(true);
    try {
      // Establish a body baseline: the MODE (most common size). Median
      // gets skewed when most of the doc is headings.
      const sizeCounts = new Map<number, number>();
      for (const l of lines) {
        const k = Math.round(l.size);
        sizeCounts.set(k, (sizeCounts.get(k) ?? 0) + 1);
      }
      const body = [...sizeCounts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? 11;

      function makeParagraph(line: PdfLine): Paragraph {
        const text = sanitizeText(line.text);
        if (!text) {
          // Empty paragraph — keeps vertical rhythm without producing empty <w:t/>
          return new Paragraph({ children: [] });
        }

        // Heading via run-level styling only — never mix `heading: HeadingLevel.X`
        // with a custom TextRun.size, that's the OOXML conflict that breaks
        // Google Docs uploads.
        let renderSize = clampSize(line.size);
        let renderBold = line.bold;
        if (detectHeadings) {
          const ratio = renderSize / Math.max(1, body);
          if (ratio >= 1.6) {
            renderSize = Math.max(renderSize, body * 1.7);
            renderBold = true;
          } else if (ratio >= 1.3) {
            renderSize = Math.max(renderSize, body * 1.4);
            renderBold = true;
          } else if (ratio >= 1.12) {
            renderSize = Math.max(renderSize, body * 1.2);
            renderBold = true;
          }
        }

        return new Paragraph({
          alignment: AlignmentType.LEFT,
          spacing: { after: renderSize >= body * 1.3 ? 160 : 80 },
          children: [
            new TextRun({
              text,
              bold: renderBold,
              italics: line.italic,
              size: Math.round(clampSize(renderSize) * 2), // half-points
            }),
          ],
        });
      }

      // Page breaks via PageBreak runs (not `pageBreakBefore`) — robust
      // across Word, LibreOffice, and Google Docs.
      const allParagraphs: Paragraph[] = [];
      let lastPage = lines[0]?.page ?? 1;
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        if (keepBreaks && line.page !== lastPage) {
          allParagraphs.push(
            new Paragraph({
              children: [new TextRun({ children: [new PageBreak()] })],
            }),
          );
          lastPage = line.page;
        }
        allParagraphs.push(makeParagraph(line));
      }

      const doc = new Document({
        creator: 'Utility OS',
        title: file.name.replace(/\.pdf$/i, ''),
        styles: {
          default: {
            document: {
              run: { font: 'Calibri', size: 22 }, // 11pt default
            },
          },
        },
        sections: [
          {
            properties: {},
            children: allParagraphs,
          },
        ],
      });
      const blob = await Packer.toBlob(doc);
      saveAs(blob, file.name.replace(/\.pdf$/i, '') + '.docx');
      toast.success('DOCX saved');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('docx build failed:', e);
      toast.error('Failed to build DOCX — check browser console for details');
    } finally {
      setWorking(false);
    }
  }

  const totalWords = lines.reduce((s, l) => s + l.text.split(/\s+/).filter(Boolean).length, 0);
  const totalPages = lines.length ? Math.max(...lines.map((l) => l.page)) : 0;

  return (
    <div>
      <ToolHeader
        toolId="pdf-to-word"
        title="PDF → Word (.docx)"
        description="Extract text from a PDF and rebuild as a Word document. Bold/italic and heading levels preserved heuristically."
        badges={
          lines.length ? (
            <>
              <Badge>{totalPages} pages</Badge>
              <Badge>{totalWords.toLocaleString()} words</Badge>
            </>
          ) : null
        }
      />

      {!file ? (
        <Dropzone
          accept="application/pdf"
          onFiles={handleFile}
          icon={<FileText size={20} />}
          hint="Native (text-based) PDFs work best — for scans, run OCR first"
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="lg:order-2 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium truncate">{file.name}</span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setFile(null);
                  setLines([]);
                }}
              >
                Change
              </Button>
            </div>
            {working && progress < 100 && (
              <div>
                <div className="text-2xs text-fg-muted mb-1 flex items-center gap-1.5">
                  <Loader2 size={10} className="animate-spin" />
                  Extracting · {progress}%
                </div>
                <div className="h-1 bg-bg-inset rounded-full overflow-hidden">
                  <div className="h-full bg-accent transition-all" style={{ width: `${progress}%` }} />
                </div>
              </div>
            )}
            <Field label="Options">
              <label className="flex items-center justify-between text-sm cursor-pointer">
                <span className="text-fg-muted">Keep page breaks</span>
                <Switch checked={keepBreaks} onChange={setKeepBreaks} />
              </label>
              <label className="flex items-center justify-between text-sm cursor-pointer mt-2">
                <span className="text-fg-muted">Detect headings by size</span>
                <Switch checked={detectHeadings} onChange={setDetectHeadings} />
              </label>
            </Field>
            <Button
              variant="primary"
              className="w-full"
              disabled={!lines.length || working}
              loading={working}
              onClick={exportDocx}
            >
              <Download size={11} />
              Export .docx
            </Button>
            <div className="text-2xs text-fg-subtle leading-relaxed border-t border-border pt-2">
              Layout is approximate — Word handles flow, not precise PDF positioning. Tables and images aren't included.
            </div>
          </Card>

          <Card className="lg:col-span-2 lg:order-1 !p-0 overflow-hidden">
            <div className="px-4 py-2.5 border-b border-border text-xs font-medium text-fg-muted">
              Extracted preview
            </div>
            <div className="p-5 overflow-auto max-h-[560px] space-y-2">
              {lines.length === 0 ? (
                <div className="py-8 text-center text-sm text-fg-subtle">
                  {working ? 'Working…' : 'No text extracted'}
                </div>
              ) : (
                lines.slice(0, 200).map((l, i) => (
                  <div
                    key={i}
                    className="leading-relaxed"
                    style={{
                      fontSize: `${Math.max(12, Math.min(20, l.size * 0.9))}px`,
                      fontWeight: l.bold ? 700 : 400,
                      fontStyle: l.italic ? 'italic' : 'normal',
                    }}
                  >
                    {l.text}
                  </div>
                ))
              )}
              {lines.length > 200 && (
                <div className="text-2xs text-fg-subtle text-center pt-2 border-t border-border">
                  Showing first 200 lines · export for full document
                </div>
              )}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

export default PdfToWord;
